import { useState, useEffect } from 'react';
import { Button, Drawer, Typography, Space, Tag, Card } from 'antd';
import { BugOutlined, DeleteOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { debugLogger } from '@/shared/utils/debugLogger';

const { Text, Paragraph } = Typography;

export const DebugPanel = () => {
  const [visible, setVisible] = useState(false);
  const [logs, setLogs] = useState(debugLogger.getLogs());

  const refreshLogs = () => {
    setLogs(debugLogger.getLogs());
  };

  const handleClear = () => {
    debugLogger.clearLogs();
    refreshLogs();
  };

  const handleDownload = () => {
    const logsText = debugLogger.exportLogs();
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (visible) {
      refreshLogs();
    }
  }, [visible]);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'red';
      case 'warn': return 'orange';
      case 'info': return 'blue';
      default: return 'default';
    }
  };

  const getLevelEmoji = (level: string) => {
    switch (level) {
      case 'error': return '❌';
      case 'warn': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  // 只在开发环境显示
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <>
      {/* 浮动按钮 */}
      <div
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
        }}
      >
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<BugOutlined />}
          onClick={() => setVisible(true)}
          style={{
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        />
      </div>

      {/* 日志面板 */}
      <Drawer
        title={
          <Space>
            <BugOutlined />
            <span>调试日志</span>
            <Tag color="blue">{logs.length} 条</Tag>
          </Space>
        }
        placement="right"
        size="large"
        open={visible}
        onClose={() => setVisible(false)}
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={refreshLogs}
              size="small"
            >
              刷新
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              size="small"
            >
              下载
            </Button>
            <Button
              icon={<DeleteOutlined />}
              onClick={handleClear}
              danger
              size="small"
            >
              清空
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {logs.length === 0 ? (
            <Card>
              <Text type="secondary">暂无日志</Text>
            </Card>
          ) : (
            logs.map((log, index) => (
              <Card
                key={index}
                size="small"
                style={{
                  borderLeft: `3px solid ${
                    log.level === 'error' ? '#ff4d4f' :
                    log.level === 'warn' ? '#faad14' :
                    log.level === 'info' ? '#1890ff' :
                    '#d9d9d9'
                  }`,
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </Text>
                    <Tag color={getLevelColor(log.level)} style={{ margin: 0 }}>
                      {getLevelEmoji(log.level)} {log.level.toUpperCase()}
                    </Tag>
                    <Tag style={{ margin: 0 }}>{log.tag}</Tag>
                  </Space>
                  <Text strong>{log.message}</Text>
                  {log.data && (
                    <Paragraph
                      code
                      copyable
                      style={{
                        margin: 0,
                        fontSize: 11,
                        maxHeight: 200,
                        overflow: 'auto',
                        background: '#f5f5f5',
                        padding: 8,
                        borderRadius: 4,
                      }}
                    >
                      {typeof log.data === 'string' 
                        ? log.data 
                        : JSON.stringify(log.data, null, 2)}
                    </Paragraph>
                  )}
                </Space>
              </Card>
            ))
          )}
        </Space>
      </Drawer>
    </>
  );
};
